import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Theme, useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { TRANSLATIONS as T, FORM_ID } from '../../../constants'
import { CommonPageProps } from '../../../types/interface/fsm.interface'
import Loading from '../../../pages/qb/Loading/Page'
import Card from '../../../pages/qb/Ownership/Card'
import useLogic from '../../../pages/qb/Ownership/useLogic'
import FormBuilder from '@dg-shared/forms/FormBuilder'
import { LMBasicV3FSM, LDContainerV2FSM, ExternalSubmitBtn, Typography } from '@dg-shared'
import { useTranslate } from '../../../hooks/shared'

const useStyles = makeStyles()((theme: Theme) => ({
  btnExternal: {
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(5),
    },
    [theme.breakpoints.down('md')]: {
      margin: '0',
      marginTop: theme.spacing(3),
      width: '100%',
    },
  },

  annotation: {
    marginBottom: theme.spacing(2),
  },
}))

const OwnershipPageComponent: FC<CommonPageProps> = ({ service }) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const { t } = useTranslate(T.OFFER_OWNERSHIP)
  const { quote, loading, btnDisabled, setBtnDisabled, handleSetOwnership } = useLogic(service.send)

  // One layout, chosen once — this page used to render BOTH the mobile and the desktop
  // tree, each with its own <FormBuilder>, and let `Hidden` drop one. `Hidden`'s default
  // js implementation is `withWidth`, which returns null until a layout effect has run, so
  // the form mounted a commit after the page appeared and any width change swapped the
  // whole subtree and threw the user's answers away. `noSsr` reads matchMedia during
  // render, so the first render is already the correct branch.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true })

  const body = (
    <>
      <FormBuilder
        id={FORM_ID.OWNERSHIP}
        submitHandler={handleSetOwnership}
        handleBtnDisabled={setBtnDisabled}
        customSubmit
      />
      <Typography color='textSecondary'>{t('bodyInfoText')}</Typography>
    </>
  )

  const submitBtn = (
    <ExternalSubmitBtn
      text={t(`next`)}
      targetId={FORM_ID.OWNERSHIP}
      disabled={btnDisabled}
      customClassName={classes.btnExternal}
    />
  )

  if (loading) {
    return <Loading />
  }

  if (!quote) {
    return null
  }

  const card = <Card itemData={quote} colLeft={body} showDivider={isDesktop} />

  return isDesktop ? (
    <LDContainerV2FSM send={service.send} breadcrumbs breadcrumbsStepPercentage={70}>
      <Typography type='h1'>{t('title')}</Typography>
      <Typography testId='ownership-annotation' className={classes.annotation}>
        {t('annotation')}
      </Typography>
      {card}
      {submitBtn}
    </LDContainerV2FSM>
  ) : (
    <LMBasicV3FSM
      send={service.send}
      breadcrumbs
      breadcrumbsBtnText={t(`back`)}
      titleHeader={t('title')}
      paragraphSubTitle={t('annotation')}
      breadcrumbsStepPercentage={70}
    >
      {card}
      {submitBtn}
    </LMBasicV3FSM>
  )
}

export default OwnershipPageComponent
